import { FC, memo, useMemo } from 'react';

import { EmptyGameCard } from './EmptyGameCard';
import { GameCard } from './GameCard';
import { LockedGameCard } from './LockedGameCard';
import { SmallGameCard } from './SmallGameCard';
import { SmallLockedGameCard } from './SmallLockedGameCard';
import { GameV1 } from '@/api/games';
import { TCardMode } from '@/modules/game/types';
import { useCommonStore } from '@/store';

interface IGameCardFactoryProps {
  game?: GameV1;
  isEmpty?: boolean;
  isSmall?: boolean;
  withoutBottom?: boolean;
  mode?: TCardMode;
  onPlayClick?: (game: GameV1) => void;
  onDemoClick?: (game: GameV1) => void;
}

export const GameCardFactory: FC<IGameCardFactoryProps> = memo(
  ({
    game,
    isEmpty,
    isSmall,
    mode = 'dark',
    withoutBottom = false,
    onPlayClick,
    onDemoClick,
  }) => {
    const user = useCommonStore.use.user();

    const isLocked = useMemo(() => {
      if (!user || !game) return false;
      return !game.currencies[user.currency];
    }, [user, game]);

    const Card = useMemo(() => {
      switch (true) {
        case !!game && isLocked && isSmall:
          return <SmallLockedGameCard game={game} />;
        case !!game && isLocked:
          return (
            <LockedGameCard
              game={game}
              mode={mode}
              withoutBottom={withoutBottom}
            />
          );
        case isEmpty:
          return <EmptyGameCard mode={mode} />;
        case !!game && isSmall:
          return <SmallGameCard game={game} />;
        case !!game:
          return (
            <GameCard
              game={game}
              mode={mode}
              withoutBottom={withoutBottom}
              onPlayClick={() => onPlayClick && onPlayClick(game)}
              onDemoClick={() => onDemoClick && onDemoClick(game)}
            />
          );
        default:
          return null;
      }
    }, [
      game,
      isEmpty,
      isSmall,
      mode,
      withoutBottom,
      isLocked,
      onPlayClick,
      onDemoClick,
    ]);

    return Card;
  }
);
